import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Subscription, fromEvent, timer } from 'rxjs';
import localforage from 'localforage';

import { AccountMembership } from 'app/API.service.custom';
import { DataShareService } from 'services/data-share.service';
import { SupportIssuesService } from './support-issues.service';
import { SupportIssuesAlertComponent, SupportIssuesAlertData } from './support-issues-alert.component';

const CHECK_INTERVAL_MS = 30 * 60 * 1000;
const FIRST_CHECK_DELAY_MS = 20 * 1000;
const LAST_CHECK_KEY_PREFIX = 'supportIssues:alertLastCheck:';

/**
 * Every 30 minutes (foreground only), for the signed-in Salesmax employee, count
 * open issues assigned to them and how many were created since the previous
 * check, then show a snackbar with a View CTA. Runs only while the support-issues
 * gate is open for the active account; account switches stop it and dismiss the
 * banner. The last-check time is persisted per account+user so a reload does not
 * re-announce issues already seen as "fresh".
 */
@Injectable({ providedIn: 'root' })
export class SupportIssuesAlertService {
  private started = false;
  private account: AccountMembership | null = null;
  private scope: string | null = null;
  private timerSub?: Subscription;
  private visibilitySub?: Subscription;
  private snackRef: MatSnackBarRef<SupportIssuesAlertComponent> | null = null;
  private checking = false;

  constructor(
    private readonly support: SupportIssuesService,
    private readonly dataService: DataShareService,
    private readonly snackBar: MatSnackBar,
  ) {}

  /** Idempotent; call once from the shell. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.dataService.getActiveAccount().subscribe((account) => {
      const nextScope = this.support.scopeKey(account);
      const open = this.support.isEnabled(account);
      if (nextScope === this.scope && open === !!this.timerSub) return;
      this.stopTimer();
      this.dismiss();
      this.account = account;
      this.scope = nextScope;
      if (open) this.schedule(FIRST_CHECK_DELAY_MS);
    });
    this.visibilitySub = fromEvent(document, 'visibilitychange').subscribe(() => {
      if (!this.scope || !this.support.isEnabled(this.account)) return;
      if (document.visibilityState === 'hidden') {
        this.stopTimer();
      } else {
        void this.scheduleRespectingLastCheck();
      }
    });
  }

  // ---------- scheduling ----------

  private schedule(delayMs: number): void {
    this.stopTimer();
    if (document.visibilityState === 'hidden') return;
    this.timerSub = timer(delayMs).subscribe(() => {
      void this.check().finally(() => this.schedule(CHECK_INTERVAL_MS));
    });
  }

  /** On returning to the foreground, wait only for the remainder of the 30-minute window. */
  private async scheduleRespectingLastCheck(): Promise<void> {
    let delay = FIRST_CHECK_DELAY_MS;
    try {
      const last = await localforage.getItem<number>(this.lastCheckKey());
      if (typeof last === 'number') delay = Math.max(FIRST_CHECK_DELAY_MS, CHECK_INTERVAL_MS - (Date.now() - last));
    } catch {
      // keep the default delay
    }
    this.schedule(delay);
  }

  private stopTimer(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = undefined;
  }

  private dismiss(): void {
    this.snackRef?.dismiss();
    this.snackRef = null;
  }

  private lastCheckKey(): string {
    return LAST_CHECK_KEY_PREFIX + (this.scope ?? 'none');
  }

  // ---------- the check ----------

  private async check(): Promise<void> {
    const account = this.account;
    const scope = this.scope;
    if (this.checking || !account?.userId || !scope || !this.support.isEnabled(account)) return;
    this.checking = true;
    try {
      let since = Date.now() - CHECK_INTERVAL_MS;
      try {
        const last = await localforage.getItem<number>(this.lastCheckKey());
        if (typeof last === 'number') since = last;
      } catch {
        // first run or storage unavailable: treat the last 30 minutes as fresh
      }
      const page = await this.support.queue({ isOpen: true, assigneeId: account.userId, limit: 100 });
      if (this.scope !== scope) return; // account changed mid-flight
      const now = Date.now();
      const unresolved = page.total ?? page.items.length;
      const fresh = page.items.filter((i) => (Date.parse(i.createdAt) || 0) > since).length;
      const overdue = page.items.filter((i) => SupportIssuesService.isOverdue(i, now)).length;
      localforage.setItem(this.lastCheckKey(), now).catch(() => undefined);
      if (unresolved > 0) this.show({ fresh, unresolved, overdue });
    } catch (e) {
      // Quiet failure: the next 30-minute check retries. Never surface as "no issues".
      console.warn('[SupportIssuesAlert] check failed', e);
    } finally {
      this.checking = false;
    }
  }

  private show(data: SupportIssuesAlertData): void {
    this.dismiss();
    this.snackRef = this.snackBar.openFromComponent(SupportIssuesAlertComponent, {
      data,
      panelClass: data.overdue > 0 ? 'style-warning' : 'style-success',
      horizontalPosition: 'left',
      verticalPosition: 'bottom',
    });
    this.snackRef.afterDismissed().subscribe(() => (this.snackRef = null));
  }
}
